// Learn page — interactive lessons (beginner to intermediate)
interface Lesson {
  id: string;
  title: string;
  icon: string;
  description: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  steps: LessonStep[];
}

interface LessonStep {
  fen: string;
  explanation: string;
}

const LESSONS: Lesson[] = [
  // ── BEGINNER ──────────────────────────────────────
  {
    id: 'pawn',
    title: 'Pion (Pawn)',
    icon: '♟',
    level: 'beginner',
    description: 'Pergerakan dan promosi pion',
    steps: [
      { fen: '4k3/4p3/8/8/8/8/8/4K3 w - - 0 1', explanation: '♟ Pion bergerak 1 kotak ke depan. Lihat! Pion hitam di e7 bisa maju ke e6. Pion TIDAK bisa mundur ya!' },
      { fen: '4k3/8/8/8/8/4P3/8/4K3 w - - 0 1', explanation: '⚡ Rahasia: dari posisi awal (baris ke-2), pion bisa lompat 2 kotak sekaligus! Tapi cuma sekali, langkah berikutnya cuma 1.' },
      { fen: '4k3/8/8/4p3/3P4/8/8/4K3 w - - 0 1', explanation: '⚔️ Pion menangkap secara DIAGONAL ke depan — bukan lurus! Pion putih di d4 bisa tangkap pion hitam di e5.' },
      { fen: '4k3/4P3/8/8/8/8/8/4K3 w - - 0 1', explanation: '🏆 PROMOSI! Pion yang sampai baris paling ujung bisa berubah jadi Queen, Rook, Bishop, atau Knight! Pilih yang paling kuat: Queen!' },
    ],
  },
  {
    id: 'knight',
    title: 'Kuda (Knight)',
    icon: '♞',
    level: 'beginner',
    description: 'Gerakan L dan lompatan',
    steps: [
      { fen: '4k3/8/8/8/4N3/8/8/4K3 w - - 0 1', explanation: '🐴 Kuda bergerak bentuk huruf "L": 2 kotak lurus, lalu 1 kotak menyamping. Dari e4, kuda bisa ke 8 kotak!' },
      { fen: '4k3/8/8/2N5/8/8/8/4K3 w - - 0 1', explanation: '🦘 Keunggulan kuda: BISA MELOMPATI bidak lain! Tidak seperti bidak lain yang terhalang. Kuda di c5 tetap bisa ke mana saja.' },
      { fen: '4k3/8/8/3ppp2/3pNp2/3ppp2/8/4K3 w - - 0 1', explanation: '📍 Posisi penting! Kuda di TENGAH papan mengontrol 8 kotak. Kuda di SUDUT cuma 2 kotak. Usahakan kuda selalu di tengah!' },
    ],
  },
  {
    id: 'bishop',
    title: 'Gajah (Bishop)',
    icon: '♝',
    level: 'beginner',
    description: 'Gerakan diagonal',
    steps: [
      { fen: '4k3/8/8/8/4B3/8/8/4K3 w - - 0 1', explanation: '↗️ Gajah bergerak DIAGONAL (serong) sejaujauh tidak terhalang. Setiap gajah selalu di warna kotak yang sama — gajah terang atau gajah gelap!' },
      { fen: '4k3/8/2B1B3/8/8/8/8/4K3 w - - 0 1', explanation: '💪 Dua gajah bersama = tim yang kuat! Satu jaga kotak terang, satu jaga kotak gelap. Lawan sulit menghindar.' },
    ],
  },
  {
    id: 'rook',
    title: 'Benteng (Rook)',
    icon: '♜',
    level: 'beginner',
    description: 'Gerakan horizontal dan vertikal',
    steps: [
      { fen: '4k3/8/8/8/4R3/8/8/4K3 w - - 0 1', explanation: '🏰 Benteng bergerak LURUS — ke atas, bawah, kiri, kanan sejaujauh tidak terhalang. Sangat kuat di jalur terbuka!' },
      { fen: '4k3/8/8/8/8/8/8/R3K2R w KQ - 0 1', explanation: '🏆 Benteng + Raja vs Raja = MENANG! Caranya: benteng kontrol baris, raja mendekat, musuh dipaksa ke pinggir.' },
    ],
  },
  {
    id: 'queen',
    title: 'Ratu (Queen)',
    icon: '♛',
    level: 'beginner',
    description: 'Bidak terkuat di papan',
    steps: [
      { fen: '4k3/8/8/8/4Q3/8/8/4K3 w - - 0 1', explanation: '👑 Ratu = BENTENG + GAJAH digabung! Bisa lurus DAN diagonal. Bidak paling kuat di papan — jagalah baik-baik!' },
      { fen: '4k3/8/8/8/8/8/8/4K2Q w - - 0 1', explanation: '⚠️ Hati-hati! Jangan keluarkan ratu terlalu awal. Ratu bisa dikejar-kejar kuda atau pion lawan. Kembangkan bidak kecil dulu!' },
    ],
  },
  {
    id: 'king',
    title: 'Raja (King)',
    icon: '♚',
    level: 'beginner',
    description: 'Bidak yang harus dilindungi',
    steps: [
      { fen: '4k3/8/8/8/4K3/8/8/8 w - - 0 1', explanation: '♚ Raja bergerak 1 kotak ke segala arah. Raja TIDAK BOLEH bergerak ke kotak yang sedang diserang lawan!' },
      { fen: '4k3/8/8/8/8/8/8/R3K2R w KQ - 0 1', explanation: '🏰 CASTLING! Gerakan spesial: raja lompat 2 kotak ke arah benteng, benteng melompati raja. Tujuannya: lindungi raja!' },
    ],
  },

  // ── INTERMEDIATE ──────────────────────────────────
  {
    id: 'castling',
    title: 'Castling Detail',
    icon: '🏰',
    level: 'intermediate',
    description: 'Semua aturan castling',
    steps: [
      { fen: 'r3k2r/pppppppp/8/8/8/8/PPPPPPPP/R3K2R w KQkq - 0 1', explanation: '🏰 CASTLING ada 2 macam: Kingside (O-O) raja ke g1, benteng ke f1. Queenside (O-O-O) raja ke c1, benteng ke d1.' },
      { fen: 'r3k2r/pppppppp/8/8/8/8/PPPPPPPP/R3K2R w KQkq - 0 1', explanation: '📋 SYARAT castling: (1) Raja belum pernah gerak, (2) Benteng belum pernah gerak, (3) Kosong di antara mereka, (4) Raja tidak sedang di-check, (5) Raja tidak melewati kotak yang diserang.' },
      { fen: 'r3k2r/pppppppp/8/8/8/8/PPPPPPPP/R3K2R w KQkq - 0 1', explanation: '🎮 Cara pakai: Klik raja, lalu klik kotak tujuan castling (g1 atau c1). Kotak itu akan muncul sebagai langkah legal!' },
    ],
  },
  {
    id: 'enpassant',
    title: 'En Passant',
    icon: '🎯',
    level: 'intermediate',
    description: 'Tangkapan khusus pion',
    steps: [
      { fen: '4k3/8/8/3pP3/8/8/8/4K3 w - d6 0 1', explanation: '🎯 EN PASSANT (tangkap lewat)! Pion hitam baru saja maju 2 kotak dan sekarang bersebelahan dengan pionmu. Kamu bisa tangkap "seolah-olah" dia cuma maju 1 kotak!' },
      { fen: '4k3/8/3P4/8/8/8/8/4K3 w - - 0 1', explanation: '🔄 Setelah en passant, pionmu bergerak ke kotak DI BELAKANG pion lawan — bukan ke kotak pion lawan itu sendiri.' },
      { fen: '4k3/8/8/8/3p4/4P3/8/4K3 b - e3 0 1', explanation: '⏰ PERINGATAN: En passant HANYA bisa dilakukan LANGKAH BERIKUTNYA setelah pion lawan maju 2 kotak. Kalau sudah lewat 1 giliran, tidak bisa lagi!' },
    ],
  },
  {
    id: 'check',
    title: 'Skak (Check)',
    icon: '⚡',
    level: 'intermediate',
    description: 'Cara keluar dari check',
    steps: [
      { fen: '4k3/8/8/8/8/8/4Q3/4K3 w - - 0 1', explanation: '⚡ SKAK (Check)! Raja sedang diserang oleh ratu! Kamu HARUS menyelamatkan raja. Ada 3 cara:' },
      { fen: '4k3/8/8/4K3/8/8/4Q3/8 w - - 0 1', explanation: '🏃 Cara 1: LARI! Pindahkan raja ke kotak aman yang tidak diserang siapa pun.' },
      { fen: '4k3/8/8/8/3N4/8/4Q3/4K3 w - - 0 1', explanation: '🛡️ Cara 2: BLOKIR! Taruh bidak lain di antara penyerang dan raja.' },
      { fen: '4k3/8/8/8/8/3N4/4Q3/4K3 w - - 0 1', explanation: '⚔️ Cara 3: TANGKAP! Hancurkan bidak yang menyerang raja kamu.' },
    ],
  },
  {
    id: 'checkmate',
    title: 'Skak Mat (Checkmate)',
    icon: '🏆',
    level: 'intermediate',
    description: 'Cara memenangkan permainan',
    steps: [
      { fen: 'rnb1kbnr/pppp1ppp/8/4p3/6Pq/5P2/PPPPP2P/RNBQKBNR b KQkq - 0 1', explanation: '🏆 SKAK MAT! Raja dalam check DAN tidak ada cara untuk menyelamatkannya — tidak bisa lari, tidak bisa blokir, tidak bisa tangkap. PERMAINAN SELESAI!' },
      { fen: '4k3/8/4Q3/4K3/8/8/8/8 w - - 0 1', explanation: '✅ Contoh: Raja + Ratu vs Raja. Raja musuh di pinggir papan, ratu kontrol baris, raja kita lindungi ratu. Checkmate!' },
      { fen: '4k3/8/8/8/8/8/8/R3K2R w KQ - 0 1', explanation: '✅ Contoh: Raja + 2 Benteng vs Raja. Mudah! Satu benteng kontrol baris, satu dorong raja ke pinggir.' },
    ],
  },
  {
    id: 'stalemate',
    title: 'Stalemate (Seri)',
    icon: '🤝',
    level: 'intermediate',
    description: 'Kapan permainan seri?',
    steps: [
      { fen: 'k7/8/1K6/8/8/8/8/8 w - - 0 1', explanation: '🤝 STALEMAT! Raja TIDAK sedang di-check, TAPI tidak ada kotak aman untuk bergerak. Hasilnya: SERI, bukan menang! Ini kesalahan umum pemula.' },
      { fen: 'k7/2Q5/1K6/8/8/8/8/8 w - - 0 1', explanation: '❌ Contoh: Ratu di c7 menguasai semua kotak raja hitam. Tapi raja tidak sedang di-check → STALEMAT = SERI! Hati-hati!' },
      { fen: 'k7/8/1KQ5/8/8/8/8/8 w - - 0 1', explanation: '💡 Tips: Saat kamu sudah hampir menang, pastikan raja lawan MASIH punya tempat untuk jalan. Jangan sampai kamu malah dapat seri!' },
    ],
  },

  // ── ADVANCED ──────────────────────────────────────
  {
    id: 'opening-principles',
    title: 'Prinsip Pembukaan',
    icon: '📖',
    level: 'advanced',
    description: 'Cara buka permainan dengan benar',
    steps: [
      { fen: 'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq e3 0 1', explanation: 'Prinsip 1: Kuasai pusat! e4 atau d4 adalah langkah pertama terbaik.' },
      { fen: 'rnbqkbnr/pppppppp/8/8/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 0 1', explanation: 'Prinsip 2: Kembangkan kuda dan gajah sebelum ratu. Kuda ke f3, c3; gajah ke c4, f4.' },
      { fen: 'rnbqk2r/pppp1ppp/5n2/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 1', explanation: 'Prinsip 3: Castling secepatnya! Melindungi raja dan menghubungkan benteng.' },
      { fen: 'rnbqk2r/pppp1ppp/5n2/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 1', explanation: 'Prinsip 4: Jangan gerakkan bidak yang sama dua kali di pembukaan. Jangan keluarkan ratu terlalu cepat.' },
    ],
  },
  {
    id: 'tactics-fork',
    title: 'Fork (Serangan Ganda)',
    icon: '🍴',
    level: 'advanced',
    description: 'Satu bidak menyerang dua',
    steps: [
      { fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq - 0 1', explanation: 'Fork: satu bidak menyerang dua bidak lawan sekaligus. Kuda paling sering melakukan fork.' },
      { fen: '4k3/8/8/3N4/8/8/8/4K3 w - - 0 1', explanation: 'Contoh: kuda di d5 bisa menyerang raja di e7 DAN benteng di c7 sekaligus. Lawan hanya bisa selamatkan satu!' },
      { fen: '4k3/8/8/8/4P3/8/8/4K3 w - - 0 1', explanation: 'Pion juga bisa fork! Pion menyerang dua bidak diagonal.' },
    ],
  },
  {
    id: 'tactics-pin',
    title: 'Pin (Penjepitan)',
    icon: '📌',
    level: 'advanced',
    description: 'Bidak yang tidak bisa bergerak',
    steps: [
      { fen: '4k3/4r3/8/8/8/8/4B3/4K3 w - - 0 1', explanation: 'Pin: bidak lawan tidak bisa bergerak karena akan membuka bidak yang lebih penting di belakangnya.' },
      { fen: '4k3/4r3/8/8/8/8/4B3/4K3 w - - 0 1', explanation: 'Contoh: gajah di e2 menjepit benteng di e7 ke raja di e8. Benteng tidak bisa bergerak!' },
      { fen: '4k3/8/8/8/8/8/4B3/4K3 w - - 0 1', explanation: 'Pin absolut: bidak di depan raja TIDAK BOLEH bergerak (akan membuka raja ke check). Pin relatif: bidak di depan bidak penting (ratu, benteng) sebaiknya tidak bergerak.' },
    ],
  },
  {
    id: 'tactics-skewer',
    title: 'Skewer (Tusuk)',
    icon: '🍢',
    level: 'advanced',
    description: 'Serangan melalui bidak ke bidak di belakang',
    steps: [
      { fen: '4k3/8/8/8/8/8/8/B3K3 w - - 0 1', explanation: 'Skewer: kebalikan dari pin! Bidak penting dipaksa bergerak, membuka bidak di belakangnya.' },
      { fen: '4k3/4R3/8/8/8/8/8/4K3 w - - 0 1', explanation: 'Contoh: benteng di e7 menyerang raja. Raja harus pindah, tapi di belakangnya ada benteng yang bisa ditangkap.' },
    ],
  },
  {
    id: 'endgame-basics',
    title: 'Dasar Akhir Permainan',
    icon: '🏁',
    level: 'advanced',
    description: 'Cara menang di akhir permainan',
    steps: [
      { fen: '4k3/8/8/8/8/8/4R3/4K3 w - - 0 1', explanation: 'Raja + Benteng vs Raja: Bisa checkmate! Tekan raja ke pinggir dengan benteng, dekati raja.' },
      { fen: '4k3/8/8/8/8/8/8/R3K3 w - - 0 1', explanation: 'Teknik: Benteng kontrol baris, raja mendekat. Raja musuh dipaksa ke pinggir.' },
      { fen: '4k3/8/8/8/8/4K3/8/4R3 w - - 0 1', explanation: 'Raja HARUS aktif di akhir permainan! Jangan sembunyi — raja adalah bidak penyerang.' },
      { fen: '4k3/4P3/4K3/8/8/8/8/8 w - - 0 1', explanation: 'Pion pass (tidak ada pion lawan di jalur) sangat berharga. Dorong ke promosi!' },
    ],
  },
  {
    id: 'opening-traps',
    title: 'Jebakan Pembukaan',
    icon: '🪤',
    level: 'advanced',
    description: 'Hindari jebakan umum',
    steps: [
      { fen: 'rnbqkbnr/pppp1ppp/8/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 0 1', explanation: "Scholar's Mate: Ratu h5 + gajah c4 serang f7. Lawan yang tidak tahu bisa kalah dalam 4 langkah!" },
      { fen: 'rnbqkb1r/pppppppp/5n2/8/3PP3/8/PPP2PPP/RNBQKBNR w KQkq - 0 1', explanation: 'Scandinavian Defense: 1.e4 d5? Jangan keluarkan ratu terlalu cepat! 2.exd5 Qxd5 3.Nc3 serang ratu.' },
      { fen: 'rnbqkbnr/pppp1ppp/8/4p3/4PP2/8/PPPP2PP/RNBQKBNR b KQkq f3 0 1', explanation: "King's Gambit: 1.e4 e5 2.f4!? Gambit berani — korbankan pion untuk serangan cepat." },
      { fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p3/2B1P3/5Q2/PPPP1PPP/RNB1K1NR w KQkq - 0 1', explanation: 'Wayward Queen: 1.e4 e5 2.Qh5?! Ratu keluar terlalu cepat. Nc6 pertahankan, Nf6 serang ratu.' },
    ],
  },
  {
    id: 'pawn-structure',
    title: 'Struktur Pion',
    icon: '🧱',
    level: 'advanced',
    description: 'Kekuatan dan kelemahan pion',
    steps: [
      { fen: '4k3/8/8/8/3P4/8/8/4K3 w - - 0 1', explanation: 'Pion terisolasi: tidak ada pion teman di samping. Lemah karena tidak bisa dilindungi pion lain.' },
      { fen: '4k3/8/8/8/2PP4/8/8/4K3 w - - 0 1', explanation: 'Pion berdampingan: saling melindungi. Lebih kuat dari pion terisolasi.' },
      { fen: '4k3/8/8/3P4/3P4/8/8/4K3 w - - 0 1', explanation: 'Pion bertumpuk: dua pion di kolom sama. Lemah karena terbatas geraknya.' },
      { fen: '4k3/8/8/P7/P7/8/8/4K3 w - - 0 1', explanation: 'Pion pass terhubung: saling melindungi. Sangat kuat di akhir permainan!' },
    ],
  },
  {
    id: 'piece-activity',
    title: 'Aktivitas Bidak',
    icon: '🎯',
    level: 'advanced',
    description: 'Maksimalkan kekuatan bidak',
    steps: [
      { fen: '4k3/8/8/4R3/8/8/8/4K3 w - - 0 1', explanation: 'Benteng di baris ke-7 sangat kuat! Serang pion, batasi raja musuh.' },
      { fen: '4k3/8/8/3N4/8/8/8/4K3 w - - 0 1', explanation: 'Kuda di outpost (didukung pion, tidak bisa diserang pion lawan) = sangat kuat.' },
      { fen: '4k3/8/8/4B3/8/8/8/4K3 w - - 0 1', explanation: 'Gajah di diagonal terbuka sangat kuat. Jangan blok gajahmu dengan pion sendiri.' },
      { fen: '4k3/8/8/8/8/8/8/4K2R w K - 0 1', explanation: 'Benteng terhubung = kekuatan maksimal. Benteng harus saling melindungi.' },
    ],
  },
  {
    id: 'zugzwang',
    title: 'Zugzwang',
    icon: '⛓️',
    level: 'advanced',
    description: 'Dipaksa bergerak = kalah',
    steps: [
      { fen: '8/8/8/4k3/4p3/4K3/8/8 w - - 0 1', explanation: 'Zugzwang: Siapa yang harus bergerak KALAH. Kunci di king+pawn endings.' },
      { fen: '8/8/8/4k3/3pP3/4K3/8/8 w - - 0 1', explanation: 'Opposition: raja berhadapan, siapa yang minggir kalah. Fundamental di akhir permainan!' },
      { fen: '8/8/8/3pk3/4P3/4K3/8/8 w - - 0 1', explanation: 'Putih menang karena hitam dipaksa bergerak raja, pion putih bisa dorong ke promosi.' },
    ],
  },
];

const LEVELS = [
  { id: 'beginner', label: 'Pemula', icon: '🌱' },
  { id: 'intermediate', label: 'Menengah', icon: '🌿' },
  { id: 'advanced', label: 'Lanjutan', icon: '🌳' },
];

let currentLesson: Lesson | null = null;
let currentStep = 0;

export function renderLearn(): string {
  return `
    <div class="learn-page">
      <h1 class="page-title">📚 Belajar Catur</h1>
      <p class="text-secondary mb-lg">Dari dasar sampai taktik tingkat lanjut</p>

      <div id="lesson-list">
        ${LEVELS.map(level => {
          const lessons = LESSONS.filter(l => l.level === level.id);
          return `
            <div class="level-section mb-lg">
              <h2 class="section-title">${level.icon} ${level.label}</h2>
              <div class="lesson-grid">
                ${lessons.map(l => `
                  <div class="lesson-card card" data-lesson="${l.id}">
                    <div class="lesson-icon">${l.icon}</div>
                    <div class="lesson-title">${l.title}</div>
                    <div class="lesson-desc">${l.description}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <div id="lesson-viewer" style="display:none">
        <button class="btn btn-ghost btn-sm mb-md" id="btn-back-lessons">← Kembali</button>
        <h2 id="lesson-title"></h2>
        <div class="lesson-board-container">
          <div id="lesson-board" class="board-wrapper" style="width:min(85vw,400px);height:min(85vw,400px)"></div>
        </div>
        <div class="lesson-explanation card" id="lesson-explanation"></div>
        <div class="lesson-nav">
          <button class="btn btn-ghost btn-sm" id="btn-prev-step">← Sebelumnya</button>
          <span id="step-counter" class="text-secondary"></span>
          <button class="btn btn-primary btn-sm" id="btn-next-step">Selanjutnya →</button>
        </div>
      </div>
    </div>
  `;
}

export function initLearn(): void {
  document.querySelectorAll('.lesson-card').forEach(card => {
    card.addEventListener('click', () => {
      const lessonId = (card as HTMLElement).dataset.lesson;
      openLesson(lessonId!);
    });
  });

  document.getElementById('btn-back-lessons')?.addEventListener('click', closeLesson);
  document.getElementById('btn-prev-step')?.addEventListener('click', prevStep);
  document.getElementById('btn-next-step')?.addEventListener('click', nextStep);
}

function openLesson(id: string): void {
  currentLesson = LESSONS.find(l => l.id === id) || null;
  if (!currentLesson) return;

  currentStep = 0;
  document.getElementById('lesson-list')!.style.display = 'none';
  document.getElementById('lesson-viewer')!.style.display = 'block';
  renderStep();
}

function closeLesson(): void {
  currentLesson = null;
  document.getElementById('lesson-list')!.style.display = 'block';
  document.getElementById('lesson-viewer')!.style.display = 'none';
}

function renderStep(): void {
  if (!currentLesson) return;
  const step = currentLesson.steps[currentStep];

  document.getElementById('lesson-title')!.textContent = currentLesson.title;
  document.getElementById('lesson-explanation')!.textContent = step.explanation;
  document.getElementById('step-counter')!.textContent = `${currentStep + 1} / ${currentLesson.steps.length}`;

  renderLessonBoard(step.fen);

  const prevBtn = document.getElementById('btn-prev-step') as HTMLButtonElement;
  const nextBtn = document.getElementById('btn-next-step') as HTMLButtonElement;
  if (prevBtn) prevBtn.disabled = currentStep === 0;
  if (nextBtn) nextBtn.textContent = currentStep === currentLesson.steps.length - 1 ? 'Selesai ✓' : 'Selanjutnya →';
}

function renderLessonBoard(fen: string): void {
  const boardEl = document.getElementById('lesson-board');
  if (!boardEl) return;

  const ranks = fen.split(' ')[0].split('/');
  const PIECE_MAP: Record<string, string> = {
    'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
    'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟',
  };

  let html = '<table class="board-table"><tbody>';
  for (let r = 0; r < 8; r++) {
    html += '<tr>';
    let col = 0;
    for (const ch of ranks[r]) {
      if (ch >= '1' && ch <= '9') {
        for (let i = 0; i < parseInt(ch); i++) {
          const isLight = (r + col) % 2 === 0;
          html += `<td class="square ${isLight ? 'light' : 'dark'}"></td>`;
          col++;
        }
      } else {
        const isLight = (r + col) % 2 === 0;
        const piece = PIECE_MAP[ch] || '';
        html += `<td class="square ${isLight ? 'light' : 'dark'}">${piece}</td>`;
        col++;
      }
    }
    html += '</tr>';
  }
  html += '</tbody></table>';
  boardEl.innerHTML = html;
}

function prevStep(): void {
  if (currentStep > 0) { currentStep--; renderStep(); }
}

function nextStep(): void {
  if (!currentLesson) return;
  if (currentStep < currentLesson.steps.length - 1) {
    currentStep++;
    renderStep();
  } else {
    closeLesson();
  }
}
